import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const BalanceNumberCalculator = () => {
  const [name, setName] = useState('');
  const [balanceNumbers, setBalanceNumbers] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  // Helper function to convert letters to numbers
  const letterToNumber = (letter) => {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    return alphabet.indexOf(letter.toUpperCase()) + 1;
  };

  // Helper function to calculate balance numbers
  const calculateBalanceNumbers = (name) => {
    const letters = name.toUpperCase().split('');
    const consonants = letters.filter(l => !'AEIOU'.includes(l));
    const vowels = letters.filter(l => 'AEIOU'.includes(l));

    // Calculate balance numbers
    const consonantSum = consonants.reduce((sum, letter) => sum + letterToNumber(letter), 0);
    const vowelSum = vowels.reduce((sum, letter) => sum + letterToNumber(letter), 0);
    const totalSum = consonantSum + vowelSum;

    // Calculate balance numbers
    const balanceNumbers = {
      consonantSum,
      vowelSum,
      totalSum,
      consonantPercentage: ((consonantSum / totalSum) * 100).toFixed(2),
      vowelPercentage: ((vowelSum / totalSum) * 100).toFixed(2)
    };

    setBalanceNumbers(balanceNumbers);

    // Generate chart data
    setChartData({
      labels: ['Consonants', 'Vowels', 'Total'],
      datasets: [{
        label: 'Balance Numbers',
        data: [consonantSum, vowelSum, totalSum],
        backgroundColor: [
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 99, 132, 0.5)',
          'rgba(75, 192, 192, 0.5)'
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(75, 192, 192, 1)'
        ],
        borderWidth: 1
      }]
    });
  };

  const handleInputChange = (e) => {
    setName(e.target.value);
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    calculateBalanceNumbers(name);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Balance Number Calculator | Discover Your Personal Balance</title>
        <meta name="description" content="Calculate your Balance Numbers using our Numerology Calculator. Understand the balance between your masculine and feminine energies." />
        <meta name="keywords" content="balance numbers, numerology calculator, personal balance, masculine energy, feminine energy" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">⚖️</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Balance Number Calculator</h1>
            <p className="text-lg text-purple-600 font-semibold">Discover Your Personal Balance</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="input-group">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
              Your Name:
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={name}
              onChange={handleInputChange}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter your full name"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
          >
            Calculate Balance Numbers
          </button>

          {error && (
            <div className="text-red-500 text-center mt-4">
              {error}
            </div>
          )}

          {balanceNumbers && (
            <div className="results-section mt-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Balance Numbers</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 bg-white rounded-lg shadow-md">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-purple-600 mb-2">
                      {balanceNumbers.consonantSum}
                    </div>
                    <p className="text-gray-700">Consonant Sum</p>
                  </div>
                </div>
                <div className="p-6 bg-white rounded-lg shadow-md">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-purple-600 mb-2">
                      {balanceNumbers.vowelSum}
                    </div>
                    <p className="text-gray-700">Vowel Sum</p>
                  </div>
                </div>
                <div className="p-6 bg-white rounded-lg shadow-md">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-purple-600 mb-2">
                      {balanceNumbers.totalSum}
                    </div>
                    <p className="text-gray-700">Total Sum</p>
                  </div>
                </div>
              </div>

              {chartData && (
                <div className="space-y-8 mt-12">
                  <div>
                    <h4 className="text-xl font-semibold mb-4">Balance Number Distribution</h4>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            title: {
                              display: true,
                              text: 'Balance Numbers Overview'
                            }
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-xl">
                    <h4 className="text-xl font-semibold mb-4">Your Balance Analysis</h4>
                    <div className="space-y-4">
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="font-medium">Consonant Percentage:</p>
                        <p>{balanceNumbers.consonantPercentage}%</p>
                        <p className="text-sm text-gray-600">Represents your masculine energy</p>
                      </div>
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="font-medium">Vowel Percentage:</p>
                        <p>{balanceNumbers.vowelPercentage}%</p>
                        <p className="text-sm text-gray-600">Represents your feminine energy</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </form>
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Examples & Interpretations <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 1: Balanced Name</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Name: Alexander</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Consonant Sum: 44</li>
                  <li>Vowel Sum: 28</li>
                  <li>Total: 72</li>
                  <li>Consonant %: 61.11%</li>
                  <li>Vowel %: 38.89%</li>
                </ul>
                <p className="mt-4 text-gray-700">
                  Interpretation: Alexander has a balanced name with slightly more masculine energy. This suggests a person who is both practical and creative, with good leadership qualities.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 2: High Masculine Energy</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Name: Christopher</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Consonant Sum: 66</li>
                  <li>Vowel Sum: 28</li>
                  <li>Total: 94</li>
                  <li>Consonant %: 70.21%</li>
                  <li>Vowel %: 29.79%</li>
                </ul>
                <p className="mt-4 text-gray-700">
                  Interpretation: Christopher has strong masculine energy, suggesting someone who is assertive, ambitious, and goal-oriented. They may need to work on balancing their practical nature with emotional expression.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 3: High Feminine Energy</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Name: Elizabeth</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Consonant Sum: 38</li>
                  <li>Vowel Sum: 44</li>
                  <li>Total: 82</li>
                  <li>Consonant %: 46.34%</li>
                  <li>Vowel %: 53.66%</li>
                </ul>
                <p className="mt-4 text-gray-700">
                  Interpretation: Elizabeth has strong feminine energy, indicating someone who is intuitive, compassionate, and creative. They may need to develop their assertiveness and practical skills.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Balance Number Meanings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold mb-2">Masculine Energy (Consonants)</h4>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Represents practicality and action</li>
                    <li>Associated with logic and structure</li>
                    <li>Indicates leadership qualities</li>
                    <li>Represents external expression</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold mb-2">Feminine Energy (Vowels)</h4>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Represents intuition and emotion</li>
                    <li>Associated with creativity and imagination</li>
                    <li>Indicates inner wisdom</li>
                    <li>Represents internal expression</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Tips for Understanding Your Balance Numbers</h3>
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Balanced Name (45-55% range)</h4>
                  <p className="text-gray-700">
                    If your name has a balance between 45-55% masculine and feminine energy, you likely have a well-rounded personality with equal strengths in both practical and creative aspects.
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-2">High Masculine Energy (60%+)</h4>
                  <p className="text-gray-700">
                    If your name has more than 60% masculine energy, you might be very practical and goal-oriented. Consider developing your creative and emotional sides to achieve better balance.
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-2">High Feminine Energy (60%+)</h4>
                  <p className="text-gray-700">
                    If your name has more than 60% feminine energy, you might be very intuitive and creative. Consider developing your practical and leadership skills to achieve better balance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      
      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate your Balance Numbers:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 1: Enter Your Name</h3>
              <p className="text-gray-700">
                Input your complete name in the input field.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate" button to get your Balance Numbers.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 3: Understand Results</h3>
              <p className="text-gray-700">
                Review your Balance Numbers and their interpretations in the results section.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What are Balance Numbers?</h3>
              <p className="text-gray-700">
                Balance Numbers in numerology reveal the balance between your masculine and feminine energies, helping you understand your personal harmony.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How are they calculated?</h3>
              <p className="text-gray-700">
                They are calculated by assigning numerical values to consonants and vowels in your name and analyzing their relationship.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What do they reveal?</h3>
              <p className="text-gray-700">
                They show the balance between your masculine (consonants) and feminine (vowels) energies, helping you understand your personal harmony and areas for balance.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Accurate Balance Number calculation</li>
                <li>Visual representation of balance</li>
                <li>Percentage analysis of energies</li>
                <li>Clean and intuitive interface</li>
                <li>Detailed explanation of results</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Understand personal energy balance</li>
                <li>Identify areas for improvement</li>
                <li>Gain insights into personal harmony</li>
                <li>Develop strategies for balance</li>
                <li>Enhance self-awareness</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BalanceNumberCalculator;
